import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";

import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import useCheckPermission from "../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../common/hooks/useGetPageInfo";
import DefaultApiService from "../../../../infrastructure/api/default";

const Plants = () => {
  const pageInfo = useGetPageInfo();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [dailyChartData, setDailyChartData] = useState(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    loadPlants();
  }, []);

  const loadPlants = async () => {
    try {
      setLoading(true);

      const response = await DefaultApiService.getApi(
        "SLN/Customer/Plants/GetCompletedPlants"
      );

      if (response.isSuccess) {
        setPlants(response.data || []);
      } else {
        toast.error(response.message || "خطا در دریافت لیست نیروگاه‌ها");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error loading plants:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyProduction = async (plantId) => {
    try {
      setChartLoading(true);

      const response = await DefaultApiService.getApi(
        "SLN/Customer/Plants/GetPlantDailyProduction",
        { plantId: plantId }
      );

      if (response.isSuccess) {
        setDailyChartData(response.data);

        // Prepare chart data for hourly production
        const chartData = response.data.hourlyProduction?.map(hour => ({
          hour: `${hour.hour}:00`,
          production: hour.production / 1000, // Convert to kWh
          irradiance: hour.solarIrradiance,
          temperature: hour.temperature,
        })) || [];

        setDailyChartData(prev => ({ ...prev, chartData }));
      } else {
        toast.error(response.message || "خطا در دریافت نمودار تولید روزانه");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
      console.error("Error loading daily production:", error);
    } finally {
      setChartLoading(false);
    }
  };

  const handleShowChart = async (plant) => {
    setSelectedPlant(plant);
    setChartModalOpen(true);
    await loadDailyProduction(plant.id);
  };

  const handleCloseChart = () => {
    setChartModalOpen(false);
    setSelectedPlant(null);
    setDailyChartData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "آنلاین":
        return "success";
      case "آفلاین":
        return "error";
      case "در حال سرویس":
        return "warning";
      case "آماده بهره‌برداری":
        return "info";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fa-IR");
  };

  const formatNumber = (num) => {
    return num ? num.toLocaleString("fa-IR") : "0";
  };

  if (loading) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} md={12}>
          <BreadcrumbsComponent />
        </Grid>
        <Grid item xs={12} md={12}>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo?.title || "لیست نیروگاه‌های تکمیل شده"}
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  تعداد نیروگاه‌ها: {plants.length}
                </Typography>
              </Box>
            }
          />
          <CardContent>
            <Grid container>
              {plants.length > 0 ? (
                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>نام نیروگاه</TableCell>
                          <TableCell align="center">تاریخ درخواست</TableCell>
                          <TableCell align="center">تاریخ بهره‌برداری</TableCell>
                          <TableCell align="center">ظرفیت (kW)</TableCell>
                          <TableCell align="center">سریال لاگر</TableCell>
                          <TableCell align="center">وضعیت</TableCell>
                          <TableCell align="center">تولید کل (kWh)</TableCell>
                          <TableCell align="center">بازدهی (%)</TableCell>
                          <TableCell align="center">عملیات</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plants.map((plant) => (
                          <TableRow key={plant.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="body1" fontWeight="bold">
                                  {plant.plantName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {plant.location}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              {formatDate(plant.requestDate)}
                            </TableCell>
                            <TableCell align="center">
                              {formatDate(plant.operationDate)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                              {formatNumber(plant.capacity)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontFamily: "monospace" }}>
                              {plant.loggerSerial}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={plant.status}
                                color={getStatusColor(plant.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {formatNumber(plant.totalProduction)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${plant.efficiency}%`}
                                color={plant.efficiency >= 20 ? "success" : plant.efficiency >= 15 ? "warning" : "error"}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleShowChart(plant)}
                              >
                                <TrendingUpIcon />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Box textAlign="center" p={4}>
                    <Typography variant="h6" color="text.secondary">
                      هیچ نیروگاهی یافت نشد
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Chart Modal */}
      <Dialog
        open={chartModalOpen}
        onClose={handleCloseChart}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedPlant && (
            <Box>
              <Typography variant="h6">
                نمودار تولید روزانه - {selectedPlant.plantName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                تاریخ: {new Date().toLocaleDateString("fa-IR")}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {chartLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : dailyChartData && dailyChartData.chartData ? (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h6" color="primary">
                      {formatNumber(dailyChartData.totalProduction)} Wh
                    </Typography>
                    <Typography variant="body2">تولید کل روزانه</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h6" color="success.main">
                      {formatNumber(dailyChartData.peakProduction)} W
                    </Typography>
                    <Typography variant="body2">پیک تولید</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h6" color="warning.main">
                      {dailyChartData.averageProduction.toFixed(1)} W
                    </Typography>
                    <Typography variant="body2">میانگین تولید</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="h6" color="info.main">
                      {dailyChartData.weatherCondition}
                    </Typography>
                    <Typography variant="body2">وضعیت آب و هوا</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Chart */}
              <Box sx={{ width: "100%", height: 400 }}>
                <ResponsiveContainer>
                  <AreaChart data={dailyChartData.chartData}>
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [
                        `${value} ${name === 'production' ? 'kWh' : name === 'irradiance' ? 'W/m²' : '°C'}`,
                        name === 'production' ? 'تولید' : name === 'irradiance' ? 'تابش خورشیدی' : 'دما'
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="production"
                      stackId="1"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          ) : (
            <Box textAlign="center" p={4}>
              <Typography variant="body1" color="text.secondary">
                داده‌ای برای نمایش وجود ندارد
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default Plants;